import React, { useState } from "react";

import { CaseStageEnum } from "../../../api/caseStages";
import ButtonV2 from "../../../components/ui/button/v2";
import { CrossButton } from "../../../components/ui/button/v2/CrossButton";
import { cn } from "../../../lib/utils";
import CaseStageProgressionDetails from "./CaseStageProgressionDetails";

export interface StageWorkflowConfirmCaseProgressionModalProps {
  isOpen: boolean;
  nextStage: CaseStageEnum;
  onClose: () => void;
  onConfirm: (closingRemarks: string) => void;
  primaryDisabled?: boolean;
  primaryLoading?: boolean;
  className?: string;
  title?: string;
  description?: string;
}

const StageWorkflowConfirmCaseProgressionModal: React.FC<
  StageWorkflowConfirmCaseProgressionModalProps
> = ({ isOpen, nextStage, onClose, onConfirm, primaryDisabled, primaryLoading, className, title, description }) => {
  const [closingRemarks, setClosingRemarks] = useState("");

  const handleDiscardClick= () => {
     setClosingRemarks("");
     onClose();
  }

  // useEffect(() => {
  //   if (!isOpen) setClosingRemarks("");
  // }, [isOpen]);

  const renderHeader = () => (
    <div className="flex flex-col gap-4">
      <div className="font-sans text-lg font-semibold leading-[100%] tracking-normal text-[#0A0A0A]">
       {title || "Confirm Case Progression"}
      </div>
      <div className="font-sans text-sm font-normal leading-5 tracking-normal text-[#737373]">
      {description || "You are completing the actions assigned to you"}
      </div>
    </div>
  );

  const renderClosingRemarks = () => (
    <div className="flex flex-col gap-2">
      <div className="font-sans text-sm font-medium leading-[100%] tracking-normal text-[#0A0A0A]">
        Closing Remarks (Optional)
      </div>
      <textarea
        value={closingRemarks}
        onChange={(e) => setClosingRemarks(e.target.value)}
        placeholder="Add final notes for the next officer..."
        className={cn(
          "w-[485px] h-[73px] resize-none rounded-md border border-[#E5E5E5] bg-white px-3 py-1",
          "font-sans text-sm font-normal leading-5 text-[#0A0A0A]",
          "placeholder:font-sans placeholder:text-sm placeholder:font-normal placeholder:leading-5 placeholder:text-[#737373]",
          "focus:outline-none focus:ring-2 focus:ring-indigo-200",
        )}
      />
    </div>
  );

  const handleConfirm = () => {
    onConfirm(closingRemarks);
  };

  const renderDiscardButton = () => (
    <ButtonV2
      variant="outline"
      size="md"
      onClick={handleDiscardClick}
      disabled={primaryLoading} 
      className={cn(
        "h-9 w-[238.5px] border border-[#3551F3] bg-[#EBEEFE] px-4 py-2",
        "font-sans text-sm font-medium leading-5 text-[#3551F3]",
        "hover:bg-gray-50",
      )}
    >
      Discard
    </ButtonV2>
  );

  const renderConfirmButton = () => (
    <ButtonV2
      variant="primary"
      size="md"
      onClick={handleConfirm}
      disabled={primaryDisabled}
      loading={primaryLoading} 
      className={cn(
        "h-9 w-[238.5px] border-none px-4 py-2",
        "bg-[#EBEEFE] border-[#3551F3] text-[#FFFFFF]",
        "hover:bg-[#EBEEFE] hover:text-[#FFFFFF]",
      )}
    >
      Confirm & Forward
    </ButtonV2>
  );

  const renderFooter = () => (
    <div className="flex gap-2 w-[485px]">
      {renderDiscardButton()}
      {renderConfirmButton()}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[rgba(10,10,10,0.20)] flex items-center justify-center z-[9999]">
      <div
        className={cn(
          "relative bg-white border border-[#E5E5E5] rounded-lg shadow-lg p-6",
          "w-[533px] h-[393px] overflow-y-auto",
          "flex flex-col gap-4",
          className,
        )}
      >
        <CrossButton onClick={handleDiscardClick}  disabled={primaryLoading} />
        {renderHeader()}
        <CaseStageProgressionDetails nextStage={nextStage} />
        {renderClosingRemarks()}
        {renderFooter()}
      </div>
    </div>
  );
};

export default StageWorkflowConfirmCaseProgressionModal;
